<template>
    <div class="container flex flex-col max-w-7xl mx-auto mt-10 px-4 pt-4 bg-slate-100 border-2 border-gray-200 rounded-lg shadow-md">
        
        <!-- Message d'erreur si pas admin -->
        <div v-if="!estAdmin && verificationTerminee" class="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            ⚠️ Accès refusé. Vous devez avoir des droits administrateur pour accéder à cette section.
        </div>

        <!-- Loader pendant la vérification -->
        <div v-if="!verificationTerminee" class="mb-6 p-4 bg-blue-100 border border-blue-400 text-blue-700 rounded">
            🔄 Vérification des droits d'accès...
        </div>

        <!-- Interface admin (visible seulement si admin) -->
        <template v-if="estAdmin && verificationTerminee">
            <div v-if="message" class="mb-6 text-navigation font-bold text-left text-base md:text-lg lg:text-xl">{{ message }}</div>

            <div class="w-full mx-auto px-4 p-6">
                <div class="flex flex-wrap w-full justify-left items-center gap-4 mb-4">
                    <button @click="changerFiltre('ajouter')" class="bg-cyan-600 text-white px-4 py-2 rounded hover:bg-blue-600 ml-2" aria-label="Ajouter un chat" title="Ajouter un chat">Ajouter un chat</button>
                    <button @click="changerFiltre('modifier')" class="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 ml-2" aria-label="Modifier un chat">Modifier un chat</button>
                    <div v-if="filtre === 'modifier'" class="flex items-center gap-2 ml-4">
                        <input v-model="noIdentificationRecherche" type="text" placeholder="No identification" class="border rounded px-2 py-1" />
                        <button @click="rechercherChat" class="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-700" aria-label="Rechercher un chat" title="Rechercher un chat">Rechercher</button>
                    </div>
                    <button @click="changerFiltre('supprimer')" class="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 ml-2" aria-label="Supprimer un chat" title="Supprimer un chat">Supprimer un chat</button>
                </div>
                <div class="flex flex-wrap w-full justify-left items-center gap-4 mb-4">
                    <button @click="changerFiltre('chats')" class="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 ml-2" aria-label="Afficher tous les chats" title="Afficher tous les chats">Tous les chats</button>
                    <button @click="changerFiltre('femelles')" class="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 ml-2" aria-label="Afficher les chats femelles" title="Afficher les chats femelles">Chats femelles</button>
                    <button @click="changerFiltre('mâles')" class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 ml-2" aria-label="Afficher les chats mâles" title="Afficher les chats mâles">Chats mâles</button>
                    <button @click="changerFiltre('adoptes')" class="bg-pink-500 text-white px-4 py-2 rounded hover:bg-pink-600 ml-2" aria-label="Afficher les chats adoptés" title="Afficher les chats adoptés">Chats adoptés</button>
                    <button @click="changerFiltre('craintifs')" class="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 ml-2" aria-label="Afficher les chats craintifs" title="Afficher les chats craintifs">Chats craintifs</button>
                    <button @click="changerFiltre('chatons_0_5_mois')" class="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400 ml-2" aria-label="Afficher les chatons de 0 à 5 mois" title="Afficher les chatons de 0 à 5 mois">Chatons 0-5 mois</button>
                    <button @click="changerFiltre('chatons_6_12_mois')" class="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400 ml-2" aria-label="Afficher les chatons de 6 à 12 mois" title="Afficher les chatons de 6 à 12 mois">Chatons 6-12 mois</button>
                </div>
            </div>

            <ModifierChat
                v-if="chatAModifier && chatTrouve"
                :chat="chatTrouve"
                @fermer="chatAModifier = false; filtre = ''"
                @sauvegarder="sauvegarderChat"
            />
            
            <div>
                <AjouterChat 
                v-if="filtre === 'ajouter'"
                @fermer="filtre = ''"
                @ajouterChat="gererAjoutChat" />
                <SupprimerChat v-if="filtre === 'supprimer'" @fermer="filtre = ''" />
                <AfficherListeChats v-if="filtre === 'chats'" />
                <AfficherChatsFemelles v-if="filtre === 'femelles'" />
                <AfficherChatsMales v-if="filtre === 'mâles'" />
                <AfficherChatsAdoptes v-if="filtre === 'adoptes'" />
                <AfficherChatsCraintifs v-if="filtre === 'craintifs'" />
                <AfficherChatons0_5Mois v-if="filtre === 'chatons_0_5_mois'" />
                <AfficherChatons6_12Mois v-if="filtre === 'chatons_6_12_mois'" />
            </div>
        </template>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { auth } from '@/firebase.js';
import { useListeChats, verifierDroitsAdmin } from '@/composables/apiListeChats.js';
import AfficherListeChats from '../views/AfficherListeChats.vue';
import AfficherChatsFemelles from '../views/AfficherChatsFemelles.vue';
import AfficherChatsMales from '../views/AfficherChatsMales.vue';
import AfficherChatsAdoptes from '../views/AfficherChatsAdoptes.vue';
import AfficherChatsCraintifs from '../views/AfficherChatsCraintifs.vue';
import AfficherChatons0_5Mois from '../views/AfficherChatons0_5Mois.vue';
import AfficherChatons6_12Mois from '../views/AfficherChatons6_12Mois.vue';
import AjouterChat from './AjouterChat.vue';
import ModifierChat from './ModifierChat.vue';
import SupprimerChat from './SupprimerChat.vue';

const filtre = ref('');
const estAdmin = ref(false);
const verificationTerminee = ref(false);

const message = ref("Bienvenue dans la section d'administration des chats! Ici, vous pouvez gérer les informations des chats, ajouter de nouveaux chats, modifier ou supprimer des entrées existantes. Sélectionnez un filtre pour afficher la liste correspondante.");
const afficherMessage = ref(true);

const { listeChats, chargerlisteChats, mettreAJourPartiellement } = useListeChats();
const noIdentificationRecherche = ref('');
const chatAModifier = ref(false);
const chatTrouve = ref(null);

// Vérifier les droits admin au montage du composant
onMounted(async () => {
    try {
        estAdmin.value = await verifierDroitsAdmin();
        verificationTerminee.value = true;

        if (!estAdmin.value) {
            message.value = "Accès refusé. Droits administrateur requis.";
        }
    } catch (error) {
        console.error("Erreur lors de la vérification des droits:", error);
        estAdmin.value = false;
        verificationTerminee.value = true;
        message.value = "Erreur lors de la vérification des droits d'accès.";
    }
});

// Fonction pour changer de filtre avec vérification
async function changerFiltre(nouveauFiltre) {
    // Vérifier à nouveau les droits avant chaque action sensible
    if (['ajouter', 'modifier', 'supprimer'].includes(nouveauFiltre)) {
        const adminActuel = await verifierDroitsAdmin();
        if (!adminActuel) {
            message.value = "Droits administrateur requis pour cette action.";
            return;
        }
    }
    filtre.value = nouveauFiltre;
}

// Gérer l'ajout d'un chat
function gererAjoutChat(chatAjoute, messageSucces) {
    console.log("Chat ajouté reçu dans ListeChatsAdmin.vue :", chatAjoute);
    console.log("Message reçu :", messageSucces);
    console.log("🎯 gererAjoutChat appelé avec :", messageSucces);
    console.log("📝 Ancien message.value :", message.value);
    message.value = messageSucces || "Chat ajouté avec succès !";
    console.log("✅ Nouveau message.value :", message.value);
    // Fermer le formulaire après ajout
    filtre.value = '';
    // Recharger la liste des chats
    chargerlisteChats();
    // Réinitialiser le message après 5 secondes
    setTimeout(() => {
        message.value = "Ici, vous pouvez gérer les informations des chats, ajouter de nouveaux chats, modifier ou supprimer des entrées existantes. Sélectionnez un filtre pour afficher la liste correspondante.";
        console.log("🗑️ Message réinitialisé après 5 secondes");
    }, 5000);
}

async function rechercherChat() {
    if (!await verifierDroitsAdmin()) {
        message.value = "Droits administrateur requis pour modifier un chat.";
        return;
    }

    if (!noIdentificationRecherche.value) {
        message.value = "Veuillez entrer un numéro d'identification pour modifier un chat.";
        afficherMessage.value = true;
        return;
    }

    await chargerlisteChats();
    const chatsArray = Array.isArray(listeChats.value) ? listeChats.value : [];

    const chat = chatsArray.find(
        c => String(c.no_identification) === String(noIdentificationRecherche.value)
    );

    if (chat) {
        chatTrouve.value = { ...chat };
        console.log("Chat trouvé :", chatTrouve.value);
        chatAModifier.value = true;
    } else {
        message.value = "Aucun chat trouvé avec ce numéro d'identification.";
        afficherMessage.value = true;
        chatAModifier.value = false;
    }
}

async function sauvegarderChat(chatModifie) {
    try {
        // Vérifier les droits admin avant la sauvegarde
        if (!await verifierDroitsAdmin()) {
            alert('Droits administrateur requis pour modifier un chat.');
            return;
        }

        const user = auth.currentUser;
        if (!user) {
            alert('Vous devez être connecté pour modifier un chat.');
            return;
        }

        // Conversion oui/non en 1/0 pour le champ reserve
        if (chatModifie.reserve === "oui") {
            chatModifie.reserve = 1;
        } else if (chatModifie.reserve === "non") {
            chatModifie.reserve = 0;
        }

        // Appel API pour modifier le chat
        const result = await mettreAJourPartiellement(chatModifie.id, chatModifie);

        if (result.success === true || (result.message && result.message.toLowerCase().includes('succès'))) {
            await chargerlisteChats();
            chatAModifier.value = false;
            filtre.value = '';
            message.value = "Chat modifié avec succès !";
        } else {
            alert(`Erreur : ${result.message}`);
        }
    } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
    }
}
</script>